import React, { useState } from 'react'
import { Modal, Button, Form, Input, Space, Typography, Divider, Card, Table, Tag, List, Avatar, Steps } from 'antd'
import { CheckCircleOutlined, CloseCircleOutlined, UserOutlined, FileTextOutlined } from '@ant-design/icons'
import { formatCurrency, formatDate } from '../../../InventoryItems/utils/helpers'
import { purchaseOrderStatuses } from '../../utils/constants'

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input
const { Step } = Steps

const OrderApprovalModal = ({ visible, onCancel, onApprove, onReject, order }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [approvalAction, setApprovalAction] = useState(null) // 'approve' or 'reject'

  // Handle approval
  const handleApproval = () => {
    setLoading(true)
    form
      .validateFields()
      .then((values) => {
        // Call the appropriate function based on user choice
        if (approvalAction === 'approve') {
          onApprove(values.comments)
        } else if (approvalAction === 'reject') {
          onReject(values.comments)
        }

        // Reset form and state
        form.resetFields()
        setApprovalAction(null)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const renderOrderSummary = () => {
    if (!order) return null

    return (
      <Space direction='vertical' style={{ width: '100%' }}>
        <Card>
          <Title level={5}>Order Summary</Title>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <p>
                <strong>Order ID:</strong> {order.id}
              </p>
              <p>
                <strong>Supplier:</strong> {order.supplierName}
              </p>
              <p>
                <strong>Order Date:</strong> {formatDate(order.orderDate)}
              </p>
              <p>
                <strong>Department:</strong> {order.department}
              </p>
            </div>
            <div>
              <p>
                <strong>Status:</strong>
                <Tag color={purchaseOrderStatuses.find((s) => s.value === order.status)?.color}>{order.status}</Tag>
              </p>
              <p>
                <strong>Items:</strong> {order.itemsOrdered.length}
              </p>
              <p>
                <strong>Total:</strong> {formatCurrency(order.totalCost)}
              </p>
            </div>
          </div>

          {order.notes && (
            <div style={{ marginTop: 16 }}>
              <Text strong>Notes:</Text>
              <Paragraph>{order.notes}</Paragraph>
            </div>
          )}
        </Card>

        <Card title='Order Items' size='small'>
          <Table
            dataSource={order.itemsOrdered}
            rowKey='itemId'
            size='small'
            pagination={false}
            columns={[
              {
                title: 'Item',
                dataIndex: 'name',
                key: 'name'
              },
              {
                title: 'Quantity',
                dataIndex: 'quantity',
                key: 'quantity'
              },
              {
                title: 'Unit Cost',
                dataIndex: 'unitCost',
                key: 'unitCost',
                render: (cost) => formatCurrency(cost)
              },
              {
                title: 'Total',
                dataIndex: 'totalCost',
                key: 'totalCost',
                render: (cost) => formatCurrency(cost)
              }
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong>{formatCurrency(order.totalCost)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </Card>

        <Card title='Approval Workflow' size='small'>
          {order.approvalChain && order.approvalChain.length > 0 ? (
            <Steps
              direction='vertical'
              size='small'
              current={order.approvalChain.filter((step) => step.status === 'Approved').length}
            >
              {order.approvalChain.map((step, index) => (
                <Step
                  key={index}
                  title={step.approverName}
                  status={step.status === 'Approved' ? 'finish' : step.status === 'Rejected' ? 'error' : 'wait'}
                  description={
                    <div>
                      <div>
                        Status:{' '}
                        <Tag color={step.status === 'Approved' ? 'green' : step.status === 'Rejected' ? 'red' : 'blue'}>
                          {step.status}
                        </Tag>
                      </div>
                      {step.timestamp && <div>Date: {formatDate(step.timestamp.split('T')[0])}</div>}
                    </div>
                  }
                />
              ))}
            </Steps>
          ) : (
            <Text>No approval workflow defined for this order.</Text>
          )}
        </Card>
      </Space>
    )
  }

  return (
    <Modal
      title='Purchase Order Approval'
      visible={visible}
      onCancel={() => {
        form.resetFields()
        setApprovalAction(null)
        onCancel()
      }}
      footer={null}
      width={700}
    >
      {renderOrderSummary()}

      <Divider />

      <Form form={form} layout='vertical'>
        <Form.Item name='comments' label='Comments'>
          <TextArea rows={3} placeholder='Add any approval comments here' />
        </Form.Item>

        <Form.Item>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Space>
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => {
                  setApprovalAction('reject')
                  handleApproval()
                }}
                loading={loading && approvalAction === 'reject'}
              >
                Reject
              </Button>
              <Button
                type='primary'
                icon={<CheckCircleOutlined />}
                onClick={() => {
                  setApprovalAction('approve')
                  handleApproval()
                }}
                loading={loading && approvalAction === 'approve'}
              >
                Approve
              </Button>
            </Space>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default OrderApprovalModal
